import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { paths, type Department } from "@clevis/shared-types";
import { Select } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { useAuth } from "./useAuth";
import { useEntitlements } from "./useEntitlements";
import { useUiPrefs } from "./useUiPrefs";

// Departments everywhere: the company's departments (jobs and requests route
// to these) and cross-department groups (Operations, Sales: people only), plus
// the one the person is looking at. That choice is remembered on their
// account, so Ben opens Jobs, Schedule, Timesheets and the dashboard on
// Fireplaces every time. Until they pick, it's the department they're in.

export type DeptRow = Department & { id: string };
export const ALL = "all";
export const NONE = "none";

export function useDepartments() {
  const { tenantId, user } = useAuth();
  const { hasFeature } = useEntitlements();
  const { prefs, loaded, save } = useUiPrefs();
  const [rows, setRows] = useState<DeptRow[]>([]);

  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(collection(db, paths.tenantDepartments(tenantId)), (snap) =>
      setRows(
        snap.docs
          .map((d) => {
            const data = d.data() as Partial<Department>;
            return { ...(data as Department), id: d.id, kind: data.kind ?? "department", managerUids: data.managerUids ?? [], memberUids: data.memberUids ?? [] };
          })
          .sort((a, b) => a.name.localeCompare(b.name)),
      ),
    );
  }, [tenantId]);

  const enabled = hasFeature("departments") && rows.length > 0;
  const departments = rows.filter((d) => d.kind !== "group");
  const groups = rows.filter((d) => d.kind === "group");
  const mine = rows.find((d) => user && (d.memberUids.includes(user.uid) || d.managerUids.includes(user.uid)) && d.kind !== "group");
  const saved = prefs.department;
  const current = !enabled ? ALL : saved && (saved === ALL || saved === NONE || rows.some((d) => d.id === saved)) ? saved : mine?.id ?? ALL;

  const peopleIn = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const d of rows) map.set(d.id, new Set([...d.memberUids, ...d.managerUids]));
    return map;
  }, [rows]);
  const inAnyDept = useMemo(() => new Set(departments.flatMap((d) => [...d.memberUids, ...d.managerUids])), [departments]);

  /** Does a person belong to the current choice? */
  const personMatches = (uid: string, choice = current) =>
    choice === ALL ? true : choice === NONE ? !inAnyDept.has(uid) : peopleIn.get(choice)?.has(uid) ?? false;

  /** Does a job belong to the current choice? (A group: jobs assigned to its people.) */
  const jobMatches = (job: { departmentId?: string | null; assignedUids?: string[] }, choice = current) => {
    if (choice === ALL) return true;
    if (choice === NONE) return !job.departmentId;
    const d = rows.find((x) => x.id === choice);
    if (d?.kind === "group") return (job.assignedUids ?? []).some((u) => peopleIn.get(choice)?.has(u));
    return job.departmentId === choice;
  };

  return {
    enabled,
    loaded,
    departments,
    groups,
    current,
    currentName: current === ALL ? "All departments" : current === NONE ? "No department" : rows.find((d) => d.id === current)?.name ?? "",
    /** The department to put new work in (never a group). */
    defaultForNew: departments.some((d) => d.id === current) ? current : "",
    setCurrent: (id: string) => save({ department: id }),
    personMatches,
    jobMatches,
  };
}

/** The one department picker (remembered per person). Renders nothing when departments are off. */
export function DepartmentFilter({ dept, className }: { dept: ReturnType<typeof useDepartments>; className?: string }) {
  if (!dept.enabled) return null;
  return (
    <Select className={className ?? "w-auto py-1.5 text-sm"} aria-label="Department" value={dept.current} onChange={(e) => dept.setCurrent(e.target.value)}>
      <option value={ALL}>All departments</option>
      <option value={NONE}>No department</option>
      {dept.departments.length ? (
        <optgroup label="Departments">
          {dept.departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </optgroup>
      ) : null}
      {dept.groups.length ? (
        <optgroup label="Groups">
          {dept.groups.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </optgroup>
      ) : null}
    </Select>
  );
}
